/**
 *
 * PrintTickets
 *
 */
import { Tooltip, Typography } from 'antd';
import { ReactComponent as DownloadIcon } from 'app/assets/img/download.svg';
import { LoadingContainer } from 'app/components/LoadingContainer';
import { StyledStack } from 'app/components/Stack';
import StyledTable from 'app/components/StyledTable';
import useGetApiData from 'app/hooks/useGetApiData';
import {
  selectInfoUser,
  selectTranslations,
} from 'app/pages/App/slice/selectors';
import {
  downloadAndViewPdf,
  downloadListPdfApi,
  downloadPdfApi,
  getDetailOrderApi,
  getOrderDetailApi,
} from 'app/services/checkoutApi';
import React, { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import DownLoadIcon from './_DownLoadIcon';
import { convertData, sortByKey } from './constant';
import DownLoadModal from './_DownLoadModal';
import { LoadingOutlined } from '@ant-design/icons';
import useWindowSize from 'app/hooks/useWindowSize';
import StyledButton from 'app/components/StyledButton';
import { I_User } from 'app/pages/App/slice/types';
import OTPVerification from './OTPVerification';
import { notify } from 'reapop';
import checkPrintTickets from '../utils/checkPrintTickets';
import { useGlobalFunction } from 'app/pages/App/hooks/useGlobalFunction';
import { StyledSelect } from 'app/components/Layout/NewHeader';
import Images from 'app/assets/img';

interface Props {}

// use this const as a key to store data in session storage and local storage
export const PRINT_TICKETS_GUEAST_FLOW_DATA = 'printTicketsGuestFlowData';

export const PrintTickets = memo((props: Props) => {
  const location = useLocation();
  const { id }: { id: string } = useParams();
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingDownAll, setIsLoadingDownAll] = useState(false);
  const [isLoadingViewAll, setIsLoadingViewAll] = useState(false);
  const translations = useSelector(selectTranslations);
  const [selected, setSelected] = useState<Array<any>>([]);
  const { device } = useWindowSize();
  const isMobile = device !== 'desktop';
  const infoUser: I_User | undefined = useSelector(selectInfoUser);
  const [showOtpModal, setShowOtpModal] = useState(false);
  const [isVerifyOtp, setIsVerifyOtp] = useState(false);
  const [data, setData] = useState<Record<string, any> | null>(null);
  const [rawError, setRawError] = useState<Record<string, any> | null>(null);
  const otpRef = useRef('');
  const [otp, setOtp] = useState('');
  const dispatch = useDispatch();
  const { accessPrintTicketFromEmailLink } = checkPrintTickets();
  const { locale, onChangeLanguage } = useGlobalFunction();

  const getOrderDetailPayload = useMemo(() => {
    const otpPassed = sessionStorage.getItem(PRINT_TICKETS_GUEAST_FLOW_DATA);
    sessionStorage.removeItem(PRINT_TICKETS_GUEAST_FLOW_DATA);

    if (!!otpPassed) otpRef.current = otpPassed;

    return {
      id,
      otp: otpPassed,
    };
  }, [id]);

  const {
    data: orderDetailData,
    isLoading: isLoadingDetail,
    rawError: rawErrorDetail,
  } = useGetApiData({
    api: getOrderDetailApi,
    params: getOrderDetailPayload,
  });

  useEffect(() => {
    setData(orderDetailData);
  }, [orderDetailData]);

  useEffect(() => {
    setRawError(rawErrorDetail);
  }, [rawErrorDetail]);

  const onVerifyOtp = async (otp: string) => {
    if (isVerifyOtp) return;
    try {
      setIsVerifyOtp(true);
      const { data } = await getDetailOrderApi(id, true, undefined, otp);
      setData(data);
      setRawError(null);
      otpRef.current = otp;
      setOtp(otp);
    } catch (error: any) {
      const errMsg = error?.message;
      if (typeof errMsg === 'string') {
        dispatch(notify(errMsg, 'error'));
      }
    } finally {
      setIsVerifyOtp(false);
    }
  };

  const maxFile = data?.maxFile || 10;

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  useEffect(() => {
    const totalTicket = searchParams.get('totalTicket') || '';

    if (accessPrintTicketFromEmailLink && !otp) return;

    if (Number(totalTicket) === 1) {
      downloadPdfApi(
        id,
        setIsLoading,
        undefined,
        otpRef.current,
        accessPrintTicketFromEmailLink ? '1' : undefined,
      );
    }
  }, [id, searchParams, accessPrintTicketFromEmailLink, otp]);

  const dataSources = useMemo(() => {
    const listTicketFormat = data?.items?.reduce(convertData, []);
    const listGiftFormat = data?.giftItems?.reduce(convertData, []);
    const listConcat = [...(listTicketFormat || []), ...(listGiftFormat || [])];
    const sortedList = sortByKey(listConcat, 'productName');
    return sortedList.map((item, index) => ({
      ...item,
      index,
      checkBoxDisable:
        !selected.map((res: any) => res.key).includes(item.key) &&
        selected?.length >= maxFile,
    }));
  }, [data?.giftItems, data?.items, maxFile, selected]);

  const selectedParams = useMemo(() => {
    return {
      orderId: id,
      tickets: sortByKey(selected, 'index').map(ticket => ({
        orderItemId: ticket?.orderItemId,
        code: ticket?.sapVerifyCode || ticket?.vgsTicketCode,
        vgsTicketId: ticket?.VGSTicketID,
      })),
    };
  }, [id, selected]);

  const columns = useMemo(
    () => [
      {
        title: (
          <DownLoadModal
            selectedParams={selectedParams}
            submitNumber={data?.id}
            maxFile={maxFile}
            otp={otpRef.current}
            guest={accessPrintTicketFromEmailLink ? '1' : undefined}
          />
        ),
        dataIndex: 'catalog',
        key: 'catalog',
        render: value => {
          return <div style={{ textAlign: 'left' }}>{value}</div>;
        },
      },
      {
        key: 'productName',
        render: value => {
          const objParams: Record<string, string> = {
            orderId: data?.idHash,
            orderItemId: value?.orderItemId,
            code: value?.sapVerifyCode || value?.vgsTicketCode,
            vgsTicketId: value?.vgsTicketCode || value?.sapVerifyCode,
          };

          const searchParams = new URLSearchParams(objParams);

          return (
            <div style={{ textAlign: 'left' }}>
              <a
                href={`/view-pdf/${data?.idHash}?${searchParams}`}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: '#F8B117',
                  textDecoration: 'underline',
                }}
                onClick={e => {
                  e.preventDefault();

                  if (!!otpRef.current && accessPrintTicketFromEmailLink) {
                    localStorage.setItem(
                      PRINT_TICKETS_GUEAST_FLOW_DATA,
                      JSON.stringify({
                        otp: otpRef.current,
                        guest: accessPrintTicketFromEmailLink ? '1' : '',
                      }),
                    );
                  }

                  setTimeout(() => {
                    window.open(
                      `/view-pdf/${data?.idHash}?${searchParams}`,
                      '_blank',
                    );
                  }, 100);
                }}
              >
                {/* {value?.productName + ' - Code ' + Number(value?.index + 1)} */}
                {value?.productName + ' ' + Number(value?.index + 1)}
              </a>
            </div>
          );
        },
      },
      {
        key: 'action',
        render: value => {
          const objParams: Record<string, string> = {
            orderId: data?.idHash,
            orderItemId: value?.orderItemId,
            code: value?.sapVerifyCode || value?.vgsTicketCode,
            vgsTicketId: value?.vgsTicketCode || value?.sapVerifyCode,
          };

          if (!!otpRef.current) {
            objParams.otp = otpRef.current;
          }

          if (accessPrintTicketFromEmailLink) {
            objParams.guest = '1';
          }

          const searchParams = new URLSearchParams(objParams);
          const name =
            // value?.productName + ' - Code ' + Number(value?.index + 1);
            value?.productName + ' ' + Number(value?.index + 1);
          return <DownLoadIcon searchParams={searchParams} name={name} />;
        },
      },
    ],
    [
      accessPrintTicketFromEmailLink,
      data?.id,
      data?.idHash,
      maxFile,
      selectedParams,
    ],
  );

  const downloadAllPdf = () => {
    if (data?.listPdfPath?.length > 0) {
      setIsLoadingDownAll(true);
      data?.listPdfPath?.forEach((value, index) => {
        // const timeout = 150 * index * (maxFile / 10);
        const timeout = 200 * index;
        const params = {
          id: data?.idHash,
          url: value,
          name: `${data?.id}_${data?.orderdate?.dateDMY?.replaceAll('/', '')}_${
            index + 1
          }`,
          otp: otpRef.current,
          guest: accessPrintTicketFromEmailLink ? '1' : undefined,
        };
        setTimeout(() => {
          downloadListPdfApi(params);
          index === data?.listPdfPath?.length - 1 && setIsLoadingDownAll(false);
        }, timeout);
      });
    } else {
      const name = `${data?.id}_${data?.orderdate?.dateDMY?.replaceAll(
        '/',
        '',
      )}`;
      downloadPdfApi(
        id,
        setIsLoadingDownAll,
        name,
        otpRef.current,
        accessPrintTicketFromEmailLink ? '1' : undefined,
      );
    }
  };

  const onSelectChangeCallback = value => {
    setSelected(value);
  };

  useEffect(() => {
    const isNeedOtp = rawError?.data?.errorType === 'errorOtp';
    if (isNeedOtp) setShowOtpModal(true);
    else setShowOtpModal(false);
  }, [rawError]);

  if (isLoading || isLoadingDetail) {
    return (
      <div style={styles}>
        <LoadingContainer loading>
          <div style={{ display: 'flex', flex: 1 }} />
        </LoadingContainer>
      </div>
    );
  }

  if (showOtpModal)
    return <OTPVerification onVerifyOtp={onVerifyOtp} loading={isVerifyOtp} />;

  return (
    <StyledStack style={styles}>
      <StyledStack
        style={{ background: 'white', flex: 1 }}
        padding={{ lg: '60px 30px', xs: '30px 15px' }}
      >
        <StyledStack
          direction={'column'}
          rowGap={'26px'}
          style={{ width: '100%' }}
        >
          <div
            className="d-flex align-items-center flex-wrap"
            style={{ columnGap: '36px', rowGap: '6px', width: '100%' }}
          >
            <StyledStack
              columnGap={'10px'}
              alignItem="center"
              style={{ width: 'initial' }}
            >
              <Typography style={{ fontWeight: 600, fontSize: 16 }}>
                {`${translations?.['b2c.txt.orderId'] ?? 'Đơn hàng'}: ${
                  data?.id || ''
                }`}
              </Typography>
              <Tooltip
                color="white"
                title={translations?.['b2c.pdf.download-all']}
                overlayInnerStyle={{
                  color: 'black',
                }}
              >
                <StyledStack
                  style={{ width: 20, height: 20 }}
                  onClick={downloadAllPdf}
                >
                  {isLoadingDownAll ? (
                    <LoadingOutlined style={{ color: '#F8B117' }} spin />
                  ) : (
                    <DownloadIcon style={{ cursor: 'pointer' }} />
                  )}
                </StyledStack>
              </Tooltip>
              {(infoUser?.businessPartner?.allowViewAllPdf ||
                accessPrintTicketFromEmailLink) && (
                <StyledButton
                  textColor="#fff"
                  height="40px"
                  width="auto"
                  className={isMobile ? 'p-1' : 'ms-5'}
                  onClick={() =>
                    downloadAndViewPdf(
                      id,
                      setIsLoadingViewAll,
                      otpRef.current,
                      accessPrintTicketFromEmailLink ? '1' : undefined,
                    )
                  }
                  disabled={isLoadingViewAll}
                >
                  {translations?.['b2c.txt.viewAllPdf'] ?? ''}
                </StyledButton>
              )}
            </StyledStack>
            <div>
              <StyledSelect
                bordered={false}
                value={locale}
                getPopupContainer={trigger => trigger.parentNode as HTMLElement}
                onChange={value => {
                  if (value) {
                    sessionStorage.setItem(
                      PRINT_TICKETS_GUEAST_FLOW_DATA,
                      otpRef.current.toString(),
                    );
                    setTimeout(() => {
                      onChangeLanguage(value as 'vi' | 'en');
                    }, 100);
                  }
                }}
                optionLabelProp="label"
                dropdownStyle={{
                  zIndex: 5,
                }}
                options={['vi', 'en'].map(val => ({
                  label: (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <img
                        src={val === 'vi' ? Images.FlagVN : Images.FlagUK}
                        style={{
                          width: 20,
                          height: 20,
                          borderRadius: 20,
                          marginRight: 8,
                          overflow: 'hidden',
                        }}
                        alt=""
                      />
                      <div>{val === 'vi' ? 'VIE' : 'ENG'}</div>
                    </div>
                  ),
                  value: val,
                }))}
              />
            </div>
          </div>
          <StyledStack direction={'column'} rowGap={'16px'}>
            <StyledStack width={{ lg: 'fit-content', xs: '100%' }}>
              {dataSources?.length ? (
                <StyledTable
                  dataSource={dataSources}
                  columns={columns}
                  onSelectChangeCallback={onSelectChangeCallback}
                  pagination={{
                    defaultPageSize: 10,
                    showSizeChanger: true,
                    pageSizeOptions: ['10', '20', '50', '100'],
                    onChange: () => {
                      document.body.scrollTop = 0; // For Safari
                      document.documentElement.scrollTop = 0;
                    },
                  }}
                />
              ) : (
                <div>
                  {translations?.['b2b.txt.pdfProccessing'] ?? ''}{' '}
                  <DownloadIcon style={{ marginLeft: 6 }} />
                </div>
              )}
            </StyledStack>
          </StyledStack>
        </StyledStack>
      </StyledStack>
    </StyledStack>
  );
});

const styles: React.CSSProperties = {
  position: 'absolute',
  top: 0,
  bottom: 0,
  left: 0,
  right: 0,
  background: 'white',
  zIndex: 1,
  display: 'block',
  height: 'auto',
};
